import { formatDate } from '@angular/common';
import {
  Component,
  DestroyRef,
  HostListener,
  inject,
  OnInit,
  QueryList,
  ViewChildren,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogRef,
} from '@angular/material/dialog';
import { IonInput } from '@ionic/angular';
import moment from 'moment-timezone';
import {
  BehaviorSubject,
  distinctUntilChanged,
  map,
  Observable,
  of,
  switchMap,
  take,
} from 'rxjs';
import { BANNER_TYPE } from 'src/app/components/banner/banner.constants';
import { ERRORS } from 'src/app/components/exceptions/exceptions-screen/shared/entry-errors/entry-errors.constants';
import { IOption } from 'src/app/components/shared/form/components/form-select/form-select.component';
import { BUTTON_CLICK } from 'src/app/components/shared/modal/warning-modal/warning-modal.constants';
import { IMAGE_SRC } from 'src/app/components/shared/shared-constants';
import { codAmountValidator } from 'src/app/core/custom-validators/non-zero-cod-value.validator';
import { SpecialCharacters } from 'src/app/core/enums';
import {
  DENSITY,
  DENSITY_ABB,
  DENSITY_WITH_ABB,
  STOP_TYPE,
  STOP_TYPE_ABB,
} from 'src/app/core/enums/operations.enum';
import {
  EnterpriseErrorMessage,
  InterceptError,
  InterceptErrorMessage,
  InvalidNumberMessage,
} from 'src/app/core/models/common/api.common.model';
import {
  CreateDeliveryRequest,
  CreateDeliveryResponse,
  DeliveryPackage,
} from 'src/app/core/models/operations/operations-delivery.model';
import {
  CreatePickupRequest,
  CreatePickupResponse,
  DeletePackageResponse,
  PickupPackage,
} from 'src/app/core/models/operations/operations-pickup-stop.model';
import { TableOperationsRouteDetails } from 'src/app/core/models/operations/operations-route-details.model';
import { LocationService } from 'src/app/core/services/admin/location/location.service';
import { DataService } from 'src/app/core/services/data/data.service';
import { DeliveryService } from 'src/app/core/services/operations/delivery/delivery.service';
import { PickupService } from 'src/app/core/services/operations/pickup/pickup.service';
import { SummaryService } from 'src/app/core/services/operations/summary/summary.service';
import { UserService } from 'src/app/core/services/user/user.service';
import { CommonUtils } from 'src/app/core/utils/common/common.utils';
import { TrackingNumberValidatorUtil } from 'src/app/core/utils/tracking-number/tracking-number-validator.utils';
import {
  DENSITY_LIST,
  EDIT_STOP_CONSTANTS,
  INFO_NOTICE_SCAN_CODES,
  PICKUP_SCAN_CODE,
  PLACEHOLDERS,
  SCAN_CODES,
  SIG_OBTAINED_CODE,
  TAXID_PFC_LIST,
} from './edit-operations-stop.constants';
import { EditOperationsStopData, FormData } from './edit-operations-stop.model';

/** EditOperationsStopComponent
 * This component is responsible for managing the UI and logic for editing
 * pickup and delivery stops in the operations workflow. It supports:
 * - Adding/removing packages.
 * - Handling scan codes and signature requirements.
 * - Managing tax ID visibility based on country.
 * - Validating tracking numbers and COD requirements.
 * - Creating and updating pickup/delivery requests.
 *
 * The component integrates with Angular Reactive Forms, Material Dialog,
 * and various services such as DeliveryService, PickupService, SummaryService,
 * and RuntimeServiceProvider to handle API interactions and runtime context.
 */
@Component({
  selector: 'app-edit-operations-stop',
  templateUrl: './edit-operations-stop.component.html',
  styleUrls: ['./edit-operations-stop.component.scss'],
  standalone: false,
})
export class EditOperationsStopComponent implements OnInit {
  data = inject<EditOperationsStopData>(MAT_DIALOG_DATA);
  readonly dialog = inject(MatDialog);
  readonly commonUtils = inject(CommonUtils);
  private readonly destroy = inject(DestroyRef);
  private readonly userService = inject(UserService);
  private readonly locationService = inject(LocationService);
  private readonly dialogRef = inject(
    MatDialogRef<EditOperationsStopComponent, { request: any; response: any }>,
  );
  private readonly fb = inject(FormBuilder);
  private readonly deliveryService = inject(DeliveryService);
  private readonly pickupService = inject(PickupService);
  private readonly summaryService = inject(SummaryService);
  private readonly trackNoValidator = inject(TrackingNumberValidatorUtil);
  private readonly dataService = inject(DataService);
  private get isDeliveryStop(): boolean {
    return this.data?.routeDetails?.[0]?.type === STOP_TYPE_ABB.DS;
  }
  private get isPickupStop(): boolean {
    return this.data?.routeDetails?.[0]?.type === STOP_TYPE_ABB.PS;
  }

  /** References to all tracking number inputs in the template */
  @ViewChildren('trackingInput') trackingInputs!: QueryList<IonInput>;

  /** Handles paste events for Pickup Stops (PS) where tracking number
   * is empty. Overrides amtDue and triggers scan code handling.*/
  @HostListener('paste', ['$event'])
  onPaste(event: ClipboardEvent) {
    if (
      this.data.routeDetails[0].type === 'PS' &&
      !this.data.routeDetails[0].trackingNumber
    ) {
      const clipboardData = event.clipboardData;
      const pastedText = clipboardData?.getData('text');

      // amtDue is row-level; set first row's value instead of a non-existent root control
      this.formDataArray?.controls?.[0]?.get('amtDue')?.setValue(0);

      if (pastedText) this.hideScanCode(pastedText);
    }
  }

  readonly CONSTANTS = { ...EDIT_STOP_CONSTANTS };
  readonly SRC = IMAGE_SRC;
  readonly DENSITY_LIST = DENSITY_LIST;
  readonly TAXID_PFC_LIST = TAXID_PFC_LIST;
  readonly PLACEHOLDERS = PLACEHOLDERS;
  readonly SCAN_CODES = SCAN_CODES;
  readonly SpecialCharacters = SpecialCharacters;
  readonly ERROR_LABELS = ERRORS;

  locationCode = '';
  currentDate = new Date();
  user = '';
  defaultStartTime = '';
  minDate: Date = new Date(
    this.currentDate.getFullYear(),
    this.currentDate.getMonth(),
    this.currentDate.getDate() - 30,
  );

  formArray!: FormArray;
  editTrackingDetailForm!: FormGroup;
  deliveryResponse!: CreateDeliveryResponse;
  pickupResponse!: CreatePickupResponse;

  locationTimezone!: string;
  packagedDeliveryStop: boolean = true;
  scanCodeVals: IOption[] = [];
  packageDetailsExists: boolean = true;
  deleteCalls$: Observable<DeletePackageResponse>[] = [];
  codRequiredResponse!: string[];
  interceptedPackageResponse!: string[];
  //Need to disable submit if user ONLY deletes a package
  hasClickedDelete!: boolean;

  //Need to disable delete if user has ADDED
  hasClickedAdd!: boolean;

  private readonly packageDetailsExistsSubject = new BehaviorSubject<boolean>(
    false,
  );

  packageDetailsExists$ = this.packageDetailsExistsSubject
    .asObservable()
    .pipe(distinctUntilChanged());

  showTaxId: boolean = false;

  countryCode$ = new BehaviorSubject<string>('');

  scanCodeData$!: Observable<IOption[]>;
  noPackagePickupStatusData$!: Observable<IOption[]>;

  isSignatureRequired = false;

  /**
   * Initializes the component:
   * - Loads scan code lists filtered by stop type (Pickup Stop, Pickup Status, Delivery)
   * - Sets up reactive form (use existing formData if provided, else create new)
   * - Applies dynamic validations (packages, COD overrides, clarify validation, TaxID setup)
   * - Subscribes to country changes to toggle tax details
   * - Derives user/location data and fetches service level CODs to determine COD-required responses
   * - Sets up dynamic validators for Tax ID length per row in `data` FormArray
   * - Wires value change subscriptions for pickup status checks (at form and row level)
   */
  ngOnInit() {
    this.scanCodeData$ = this.summaryService.getScanCodes().pipe(
      map((data) =>
        data.response.scan
          .filter((val) =>
            this.data.routeDetails[0].type === STOP_TYPE_ABB.PS
              ? val.ispuscan
              : this.data.routeDetails[0].type === STOP_TYPE_ABB.DS &&
                val.isdelscan,
          )
          .map((val) => ({ label: val.shortText, value: val.code }))
          .sort((a, b) => a.label.localeCompare(b.label)),
      ),
    );

    this.noPackagePickupStatusData$ = this.summaryService.getScanCodes().pipe(
      map((data) =>
        data.response.scan
          .filter(
            (val) =>
              this.data.routeDetails[0].type === STOP_TYPE_ABB.PS &&
              val.ispustopscan,
          )
          .map((val) => ({ label: val.shortText, value: val.code }))
          .sort((a, b) => a.label.localeCompare(b.label)),
      ),
    );

    this.hasClickedAdd = this.data.hasClickedAdd ?? false;
    this.hasClickedDelete = this.data.hasClickedDelete ?? false;

    this.data.country
      ?.pipe(takeUntilDestroyed(this.destroy))
      .subscribe((dat) => {
        this.countryCode$.next(dat.response?.address?.isoCode ?? '');
        this.showTaxDetails(dat.response?.address?.isoCode);
      });

    this.editTrackingDetailForm = this.createForm();

    this.addValidations();

    this.userService
      .getCurrentUser$()
      .pipe(
        take(1),
        switchMap((user) => {
          this.locationCode = user.location;
          this.user = user.loginId;
          return this.deliveryService.getServiceLevelCods().pipe(
            take(1),
            map((vals) => {
              this.codRequiredResponse = vals.response.serviceLevels.map(
                (val) => val.code,
              );
            }),
          );
        }),
      )
      .subscribe();

    // Uppercase key fields for existing rows
    (this.formDataArray.controls as FormGroup[]).forEach((fg: FormGroup) => {
      this.commonUtils.autoCapitalize(
        fg,
        ['trackingNumber', 'shipperNumber', 'description', 'taxidPfcNumber'],
        this.destroy,
      );
    });
  }

  addValidations() {
    this.checkPickupStatus();
    this.disableTrackingNumberFields();
    this.validationOnPackages();
    this.setUpClarifyValidation();
    this.setupInfoNoticeValidation();

    const dataArray = this.editTrackingDetailForm.get('data') as FormArray;
    dataArray.valueChanges.subscribe(() => this.checkPickupStatus());

    this.setValidationsForPackages();

    this.formArray = this.editTrackingDetailForm.get('data') as FormArray;
    this.formArray.updateValueAndValidity({
      onlySelf: true,
      emitEvent: false,
    });
  }

  /** Applies validations on the Clarify field depending on whether the current stop
   * contains package details and the stop type is Delivery.
   *
   * Rules:
   * - If the stop is a Delivery (DS) but no package details exist,
   *   we remove the required validator from Clarify because signature is not needed.
   *
   * - If the stop is a Delivery (DS) and package details do exist,
   *   we ensure Clarify follows its validation rules and enable signature requirement.
   *
   */
  setValidationsForPackages() {
    const isDelivery = this.isDeliveryStop;

    this.packageDetailsExists$.subscribe((exists) => {
      this.packageDetailsExists = exists;

      if (!exists && isDelivery) {
        this.editTrackingDetailForm.get('clarify')?.clearValidators();
        this.isSignatureRequired = false;
        this.editTrackingDetailForm.get('clarify')?.updateValueAndValidity();
      } else if (exists && isDelivery) {
        this.editTrackingDetailForm.get('clarify')?.updateValueAndValidity();
        this.isSignatureRequired = true;
      }
    });
  }

  /**
   * Initializes default scan code selection and clarify-related requirements.
   *
   * Behavior:
   * - Reads `scanCodeData$` once to get available scan codes and assigns them to `scanCodeVals`.
   * - If the stop is Pickup (PS), defaults `commonScanCode` to the first available code.
   * - If the stop is Delivery (DS) and has a tracking number:
   *   - Sets `commonScanCode` to `SIG_OBTAINED_CODE`.
   *   - Flags `isSignatureRequired = true`.
   *   - Triggers validity update on `clarify` control.
   *
   * Assumptions:
   * - `SIG_OBTAINED_CODE` represents the "Signature Obtained" scan option.
   * - `scanCodeData$` emits an array of `{ label, value }`.
   */
  setUpClarifyValidation() {
    this.scanCodeData$.pipe(take(1)).subscribe((val) => {
      this.scanCodeVals = val;

      const pickupScanOption = val.find(
        (option) => option.value === PICKUP_SCAN_CODE,
      );

      const defaultScanCode = pickupScanOption?.value ?? val[0]?.value;

      this.editTrackingDetailForm
        .get('commonScanCode')
        ?.setValue(defaultScanCode);

      if (this.isDeliveryStop && this.data.routeDetails[0].trackingNumber) {
        this.editTrackingDetailForm
          .get('commonScanCode')
          ?.setValue(SIG_OBTAINED_CODE);
        this.isSignatureRequired = true;
        this.editTrackingDetailForm.get('clarify')?.updateValueAndValidity();
      }

      this.scanCodeOverride(true);
    });
  }

  /**
   * Updates the `infoNotice` control state based on allowed scan codes.
   *
   * - Enables `infoNotice` when the current `scanCode` is included in
   *   `INFO_NOTICE_SCAN_CODES`.
   * - Disables `infoNotice` otherwise.
   * - Applies the same rule on initial load and whenever any package scan code changes.
   */
  setupInfoNoticeValidation() {
    const infoNoticeControl = this.editTrackingDetailForm.get('infoNotice');
    const dataArray = this.editTrackingDetailForm.get('data') as FormArray;

    const evaluateInfoNoticeState = () => {
      const hasInfoNoticePackage = dataArray.controls.some((group) => {
        const scanCode = group.get('scanCode')?.value;
        return INFO_NOTICE_SCAN_CODES.includes(scanCode);
      });

      if (hasInfoNoticePackage) {
        infoNoticeControl?.enable({ emitEvent: false });
      } else {
        infoNoticeControl?.disable({ emitEvent: false });
        infoNoticeControl?.setValue('', { emitEvent: false });
      }

      infoNoticeControl?.updateValueAndValidity({ onlySelf: true });
    };

    // Initial evaluation
    evaluateInfoNoticeState();

    // React to individual package scanCode changes
    dataArray.controls.forEach((group) => {
      group.get('scanCode')?.valueChanges.subscribe(() => {
        evaluateInfoNoticeState();
      });
    });

    //  React to commonScanCode changes
    this.editTrackingDetailForm
      .get('commonScanCode')
      ?.valueChanges.subscribe(() => {
        evaluateInfoNoticeState();
      });
  }

  /**
   * Returns the maximum length for Tax ID field based on the selected type.
   *
   * Behavior:
   * - Returns 16 for "PFC" type.
   * - Returns 13 for all other types.
   *
   * Parameters:
   * @param rowIndex The index of the row in the data FormArray.
   * @returns The maximum length for the Tax ID field.
   */
  getMaxLengthForTaxId(rowIndex: number): number {
    const taxType = this.formArray.at(rowIndex).get('taxidPfc')?.value;
    return taxType === 'PFC' ? 16 : 13;
  }

  /**
   * Toggles tax-related UI for countries requiring Tax ID.
   *
   * Behavior:
   * - Sets `showTaxId = true` when ISO code is Italy ('IT').
   *
   * Parameters:
   * @param isoCode ISO 3166 country code (e.g., 'US', 'IT').
   */
  showTaxDetails(isoCode: string | undefined) {
    if (isoCode === 'IT') {
      this.showTaxId = true;
    }
  }

  /**
   * Determines the responsive column size for fields in the grid layout.
   *
   * Behavior:
   * - Returns different sizes depending on whether `showTaxId` is enabled.
   * - Provides specific sizes per field key to keep the form compact/expanded appropriately.
   *
   * Parameters:
   * @param field Name/key of the field whose column size is requested.
   * @returns Number representing grid column size for the given field.
   */
  getColSize(field: string): number {
    switch (field) {
      case 'trackingNumber':
        return this.showTaxId ? 1.5 : 2;
      case 'shipperNumber':
        return this.showTaxId ? 0.75 : 1;
      case 'scanCode':
        return this.showTaxId ? 1.3 : 1.75;
      case 'description':
        return this.showTaxId ? 1.7 : 2.5;
      case 'taxId':
        return this.showTaxId ? 1.25 : 0;
      case 'taxIdNumber':
        return this.showTaxId ? 1.5 : 0;
      case 'paymentType':
      case 'amtDue':
      case 'amtCollected':
        return this.showTaxId ? 1 : 1.25;
      case 'actions':
        return 0.75;
      default:
        return 1;
    }
  }

  // Method to determine header column size
  getHeaderColSize(field: string): number {
    switch (field) {
      case 'trackingNumber':
        return this.showTaxId ? this.getSpan(1.5) : 2;
      case 'shipperNumber':
        return this.showTaxId ? this.getSpan(0.75) : 1;
      case 'scanCode':
        return this.showTaxId ? this.getSpan(1.3, 0.05) : 1.75;
      case 'description':
        return this.showTaxId ? this.getSpan(1.7) : 2.5;
      case 'taxId':
        return this.showTaxId ? this.getSpan(1.25, 0.1) : 0;
      case 'taxIdNumber':
        return this.showTaxId ? this.getSpan(1.5) : 0;
      case 'paymentType':
      case 'amtDue':
      case 'amtCollected':
        return this.showTaxId ? this.getSpan(1) : 1.25;
      case 'actions':
        return 0.75;
      default:
        return 1;
    }
  }

  /** Propagates the value of `commonScanCode` to all `scanCode` fields in the `data` FormArray.
   * - Avoid triggering full group validation when updating scanCode to prevent clearing errors for other fields (like tracking numbers).
   * - Preserve any existing trackingNumber errors after updating scanCode.
   */

  /**
   * Synchronizes scan code selection across the form and enforces clarify/signature behavior.
   *
   * Behavior:
   * - Defines a local helper to propagate the `commonScanCode` value to each row's `scanCode`.
   *   * Preserves any existing `trackingNumber` errors across validation cycles.
   *   * Updates `scanCode` validity without emitting value change events to avoid loops.
   * - Subscribes to `commonScanCode` changes and:
   *   * Propagates the new value to every row.
   *   * Applies clarify/signature rules via `setClarifyValidation`.
   *
   * Notes:
   * - This method assumes `editTrackingDetailForm` has a `data` FormArray
   *   with child controls named `scanCode` and `trackingNumber`.
   * - Side effect: toggles `isSignatureRequired` indirectly via `setClarifyValidation`.
   */
  scanCodeOverride(fillEmptyValues: boolean) {
    // Function to propagate commonScanCode to all scanCode fields
    const dataArray = this.editTrackingDetailForm.get('data') as FormArray;

    dataArray.controls.forEach((group) => {
      const scanCodeControl = group.get('scanCode');
      const trackingControl = group.get('trackingNumber');

      // Preserve existing trackingNumber errors before validation runs
      const prevTrackingErrors = trackingControl?.errors;

      if ((fillEmptyValues && !scanCodeControl?.value) || !fillEmptyValues) {
        //Fill in all empty/invalid scan code values
        scanCodeControl?.setValue(
          this.editTrackingDetailForm.get('commonScanCode')?.value,
          { emitEvent: false },
        );
      }

      // Recalculate validity while suppressing event emissions
      scanCodeControl?.updateValueAndValidity({
        onlySelf: true,
        emitEvent: false,
      });

      // Restore trackingNumber errors if validator cleared them
      if (prevTrackingErrors) {
        trackingControl?.setErrors({
          ...prevTrackingErrors,
          ...(trackingControl.errors ?? {}),
        });
      }
    });

    this.setClarifyValidation(
      this.editTrackingDetailForm.get('commonScanCode')?.value,
    );

    // When commonScanCode changes, we need to call setupInfoNoticeValidation here as well since it contains the logic to enable/disable infoNotice based on scan codes
    this.setupInfoNoticeValidation();
  }

  /**
   * Sets clarify-related requirements based on the selected scan code.
   *
   * Behavior:
   * - Determines whether the provided scan code represents "Signature Obtained"
   *   using the enum/constant comparison (supports uppercased enum and raw code).
   * - Toggles `isSignatureRequired` accordingly.
   * - Resets the `clarify` control and refreshes its validity to ensure the UI reflects new rules.
   *
   * Parameters:
   * @param value The scan code value selected at the common or row level.
   */
  setClarifyValidation(value: string) {
    const isSignatureCode =
      value === SCAN_CODES.SIG_OBTAINED.toUpperCase() ||
      value === SIG_OBTAINED_CODE;

    // Flag whether clarify/signature is required for the current selection
    this.isSignatureRequired = isSignatureCode;

    // Reset and re-validate clarify so rules are enforced immediately
    const clarifyControl = this.editTrackingDetailForm.get('clarify');
    if (!clarifyControl) return;

    const completionTime =
      this.editTrackingDetailForm.get('completionTime')?.value;
    const needSignatureNow = isSignatureCode && !!completionTime;

    if (needSignatureNow) {
      clarifyControl.setValidators([Validators.required]);
    } else {
      clarifyControl.clearValidators();
    }

    // Recompute validity without emitting change events if you prefer
    clarifyControl.updateValueAndValidity({ onlySelf: true, emitEvent: false });
    this.editTrackingDetailForm.updateValueAndValidity({ emitEvent: true });
  }

  /**
   * Handles visibility and validation toggles for scan code based on package presence.
   *
   * Behavior:
   * - When `data.value` is truthy (indicating packages present or a selection made):
   *   * Sets `amtDue` to 0 for the given row (or first row if index is undefined).
   *   * Disables `noPackagePickupStatus`.
   *   * Marks the stop as not packaged-only (`packagedDeliveryStop = false`).
   *   * Adds `Validators.required` to `commonScanCode`.
   * - When `data.value` is falsy (no packages/no selection):
   *   * Marks packaged-only stop (`packagedDeliveryStop = true`).
   *   * Enables `noPackagePickupStatus`.
   *   * Clears validators on `commonScanCode`.
   * - Updates validity on affected controls and on the overall form.
   *
   * Parameters:
   * @param data Arbitrary object containing a `value` key used to determine behavior.
   * @param index Optional index of the row in `formDataArray` to apply `amtDue` reset.
   */
  hideScanCode(data: any, index?: number) {
    if (data?.value) {
      // fix the length or keypress
      this.formDataArray.controls[index ?? 0].get('amtDue')?.setValue(0);
      this.editTrackingDetailForm.get('noPackagePickupStatus')?.disable();
      this.packagedDeliveryStop = false;

      this.editTrackingDetailForm
        .get('commonScanCode')
        ?.addValidators([Validators.required]);
    } else {
      this.packagedDeliveryStop = true;
      this.editTrackingDetailForm.get('noPackagePickupStatus')?.enable();
      this.editTrackingDetailForm.get('commonScanCode')?.clearValidators();
    }

    // Refresh validity for the form and dependent controls
    this.formDataArray.updateValueAndValidity();
    this.editTrackingDetailForm
      .get('noPackagePickupStatus')
      ?.updateValueAndValidity();
    this.editTrackingDetailForm.updateValueAndValidity();
  }

  /**
   * Applies enable/disable and validator rules to row-level package fields
   * based on the `noPackagePickupStatus` toggle.
   *
   * Behavior:
   * - Subscribes to `noPackagePickupStatus` value changes.
   * - For each row in `data` FormArray:
   *   * When true (no packages):
   *     - Sets `packagedDeliveryStop = true`.
   *     - Clears validators and disables: `trackingNumber`, `shipperNumber`,
   *       `scanCode`, `amtDue`, `amtCollected`, `paymentType`.
   *   * When false (packages present):
   *     - Sets `packagedDeliveryStop = false`.
   *     - Applies `Validators.required` to the same controls and enables them.
   *   * Always calls `updateValueAndValidity()` for each affected control.
   *
   * Notes:
   * - This function centralizes the rules for package vs. no-package states across all rows.
   * - Expects `editTrackingDetailForm` to contain a `data` FormArray with named child controls.
   */
  validationOnPackages() {
    // Add value change listener for noPackagePickupStatus
    this.editTrackingDetailForm
      .get('noPackagePickupStatus')
      ?.valueChanges.subscribe((value) => {
        const dataArray = this.editTrackingDetailForm.get('data') as FormArray;
        dataArray.controls.forEach((group) => {
          const trackingNumberControl = group.get('trackingNumber');
          const shipperNumberControl = group.get('shipperNumber');
          const scanCodeControl = group.get('scanCode');
          const amtDueControl = group.get('amtDue');
          const amtCollectedControl = group.get('amtCollected');
          const paymentTypeControl = group.get('paymentType');

          if (value) {
            // No packages: disable fields and clear validators
            this.packagedDeliveryStop = true;

            trackingNumberControl?.clearValidators();
            trackingNumberControl?.disable();

            shipperNumberControl?.clearValidators();
            scanCodeControl?.disable();

            scanCodeControl?.clearValidators();
            amtDueControl?.disable();

            amtDueControl?.clearValidators();
            amtCollectedControl?.disable();

            amtCollectedControl?.clearValidators();
            paymentTypeControl?.disable();

            paymentTypeControl?.clearValidators();
          } else {
            // Packages present: enable fields and require values
            this.packagedDeliveryStop = false;

            trackingNumberControl?.setValidators([Validators.required]);
            shipperNumberControl?.setValidators([Validators.required]);
            scanCodeControl?.setValidators([Validators.required]);
            amtDueControl?.setValidators([Validators.required]);
            amtCollectedControl?.setValidators([Validators.required]);
            paymentTypeControl?.setValidators([Validators.required]);
          }

          // Always refresh validity after enabling/disabling/updating validators
          trackingNumberControl?.updateValueAndValidity();
          shipperNumberControl?.updateValueAndValidity();
          scanCodeControl?.updateValueAndValidity();
          amtDueControl?.updateValueAndValidity();
          amtCollectedControl?.updateValueAndValidity();
          paymentTypeControl?.updateValueAndValidity();
        });
      });
  }

  /**
   * Builds and returns the reactive form for editing tracking details.
   *
   * Behavior:
   * - Transforms incoming route details into a normalized `formData`.
   * - Creates top-level controls (planDate, density, completionTime, etc.).
   * - Creates a `data` FormArray with row-level groups containing tracking/shipping fields,
   *   scan code, description, Tax ID fields, COD fields, and validators.
   * - Disables description for Pickup Stop (PS) mode.
   * - Applies a per-row validator for tracking number via `trackingNumberValidator`.
   *
   * Assumptions:
   * - `transformDataToFormData` returns an object with `data` suitable to be iterated.
   * - `TableOperationsRouteDetails` has fields used below (trackingNumber, shipperNumber, etc.).
   * - `maxLengthForTaxId` is initialized elsewhere (updated dynamically in getMaxLengthForTaxId).
   *
   * @returns FormGroup The fully composed reactive form.
   */
  createForm() {
    const formData = this.transformDataToFormData(this.data?.routeDetails);
    const clarifyControl = this.fb.control('');

    const isDelivery = this.data.routeDetails[0].type === STOP_TYPE_ABB.DS;
    const isPickup = this.data.routeDetails[0].type === STOP_TYPE_ABB.PS;

    // Description control disabled for Pickup Stop (PS)
    const descriptionControl = this.fb.control({ value: '', disabled: true });
    if (isPickup) {
      descriptionControl.disable();
    }

    // ----- Delivery only Info Notice validators -----
    const infoNoticeControl = this.fb.control(
      this.data?.routeDetails?.[0]?.infoNotice ?? '',
      isDelivery
        ? [
            Validators.maxLength(12), // Legacy length limit
            Validators.pattern(/^\d{0,12}$/), // DIGITS ONLY (Legacy behavior)
          ]
        : [],
    );

    // ----- Row-level unique tracking numbers -----
    const arrayLevelValidators = [
      this.trackNoValidator.uniqueTrackingNumbersValidator('trackingNumber'),
    ].filter(Boolean) as ValidatorFn[];

    return this.fb.group({
      // ----- General stop-level fields -----
      planDate: [this.data.planDate, Validators.required],
      density: [this.getDensity(), Validators.required],
      completionTime: [
        this.data.routeDetails[0].completionTime,
        Validators.minLength(5),
      ],
      stopDate: [new Date()],
      clarify: clarifyControl,

      // 🔹 FIXED: Delivery-only length & numeric validation
      // 🔹 Pickup: disabled automatically
      infoNotice: infoNoticeControl,

      noPackagePickupStatus: [''],
      commonScanCode: [''],
      pickupRequestNumber: [''],

      // ----- Row-level package details -----
      data: this.fb.array(
        (formData?.data ?? []).map((d: TableOperationsRouteDetails) => {
          const scanCodeControl = this.fb.control(d.scanCode);
          const rowDescriptionControl = this.fb.control({
            value: d.description,
            disabled: isPickup,
          });
          const taxidPfcControl = this.fb.control(TAXID_PFC_LIST[0].value);

          return this.fb.group(
            {
              trackingNumber: d.trackingNumber,
              shipperNumber: d.shipperNumber,
              scanCode: scanCodeControl,
              description: rowDescriptionControl,

              taxidPfcNumber: [d.taxid],
              taxidPfc: taxidPfcControl,

              amtDue: d.amtDue,
              amtCollected: [d.amtCollected ? d.amtCollected : 0.0],
              paymentType: [d.paymentType ?? this.CONSTANTS.CASH],

              position: d.position,
            },
            {
              validators: [
                this.trackNoValidator.trackingNumberValidator('trackingNumber'),
                this.trackNoValidator.taxIdMinLengthValidator(
                  'taxidPfcNumber',
                  'taxidPfc',
                ),
              ],
            },
          );
        }),
        { validators: Validators.compose(arrayLevelValidators) },
      ),
    });
  }

  /**
   * Indicates whether the stop can be fully completed.
   *
   * Rules:
   * - Form must be valid AND dirty, and no delete action was performed.
   * - For Pickup Stop (PS):
   *   - If package details exist → completionTime must be set.
   *   - Else (no packages) → both `noPackagePickupStatus` and `completionTime` must be set.
   * - For Delivery Stop (DS):
   *   - If signature is required → `clarify` must be set and completionTime present.
   *   - Else → completionTime present is sufficient.
   *
   * @returns boolean True if the stop can be completed based on current form state and stop type.
   */
  get canComplete(): boolean {
    //Only can fully complete a stop if user added or edited something
    const form = this.editTrackingDetailForm;
    const isValid = form.valid && form.dirty;
    const completionTime = !!form.get('completionTime')?.value;
    const stopType = this.data.routeDetails[0].type;

    if (!isValid) return false;

    if (stopType === STOP_TYPE_ABB.PS) {
      if (this.packageDetailsExists) {
        return completionTime;
      } else {
        const noPackagePickupStatus = !!form.get('noPackagePickupStatus')
          ?.value;
        return noPackagePickupStatus && completionTime;
      }
    }

    if (stopType === STOP_TYPE_ABB.DS) {
      if (this.isSignatureRequired) {
        const clarify = !!form.get('clarify')?.value;
        return clarify && completionTime;
      } else {
        return completionTime;
      }
    }

    return false;
  }

  /**
   * Indicates whether the form can be saved (not necessarily completed).
   *
   * Rules:
   * - For Pickup Stop (PS): form must be dirty and valid.
   * - For Delivery Stop (DS):
   *   - If completionTime is set AND signature required → form must be dirty, valid, and clarify present.
   *   - Else → form must be dirty and valid.
   * - For other/unknown stop types: defaults to true.
   *
   * @returns boolean True if current form is saveable based on stop type and signature rules.
   */
  get canSave(): boolean {
    if (this.data.routeDetails[0].type === STOP_TYPE_ABB.PS) {
      return (
        this.editTrackingDetailForm?.dirty && this.editTrackingDetailForm?.valid
      );
    }

    if (this.data.routeDetails[0].type === STOP_TYPE_ABB.DS) {
      if (
        !!this.editTrackingDetailForm.get('completionTime')?.value &&
        this.isSignatureRequired
      ) {
        return (
          this.editTrackingDetailForm?.dirty &&
          this.editTrackingDetailForm?.valid &&
          !!this.editTrackingDetailForm.get('clarify')?.value
        );
      } else {
        return (
          this.editTrackingDetailForm?.dirty &&
          this.editTrackingDetailForm?.valid
        );
      }
    }

    return true;
  }

  /**
   * Determines the effective Amount Due for a row based on stop type.
   *
   * Rules:
   * - Pickup Stop (PS):
   *   - Prefer `amtDeclared` when present.
   *   - Else use `d.amtDue` if it has length > 0; otherwise '0'.
   * - Delivery Stop (DS): Use `d.amtDue` when present; otherwise '0'.
   *
   * @param d Row data for a package (`TableOperationsRouteDetails`).
   * @returns string Amount due as a string (e.g., '0', '12.34').
   */
  getAmountDue(d: TableOperationsRouteDetails): string {
    const raw = this.isPickupStop ? (d.amtDeclared ?? d.amtDue) : d.amtDue;

    if (raw === null || raw === undefined) return '0';

    const s = String(raw).trim();
    return s === '' ? '0' : s;
  }

  /**
   * Disables tracking number fields for rows that already have a value.
   *
   * Behavior:
   * - Iterates over `data` FormArray rows.
   * - Disables `trackingNumber` control for rows where tracking number is present,
   *   preventing accidental edits to existing identifiers.
   */
  disableTrackingNumberFields() {
    const dataArray = this.editTrackingDetailForm.get('data') as FormArray;
    dataArray.controls.forEach((group) => {
      const trackingNumberControl = group.get('trackingNumber');
      if (trackingNumberControl?.value) {
        trackingNumberControl.disable();
      }
    });
  }

  /**
   * Maps route density abbreviation to the display value used by the form.
   *
   * Input:
   * - `R`, `C`, or `U` (Residential, Commercial, Unknown) via `this.data.routeDetails[0].density`
   *
   * Output:
   * - Corresponding `DENSITY_WITH_ABB` value for UI binding.
   * - Defaults to `DENSITY.COMMERCIAL` when unknown.
   *
   * @returns string Density string used in the UI form.
   */
  getDensity() {
    switch (this.data.routeDetails[0].density) {
      case DENSITY_ABB.R:
        return DENSITY_WITH_ABB.RESIDENTIAL;
      case DENSITY_ABB.C:
        return DENSITY_WITH_ABB.COMMERCIAL;
      case DENSITY_ABB.U:
        return DENSITY_WITH_ABB.UNKNOWN;
      default:
        return DENSITY.COMMERCIAL;
    }
  }

  /**
   * Converts a UI density value back to the response/DTO density value.
   *
   * Input:
   * - One of `DENSITY_WITH_ABB.RESIDENTIAL | COMMERCIAL | UNKNOWN`.
   *
   * Output:
   * - Corresponding `DENSITY.RESIDENTIAL | COMMERCIAL | UNKNOWN`.
   * - Defaults to `DENSITY.COMMERCIAL` when unknown.
   *
   * @param value UI density value used in the form.
   * @returns string Density value expected by backend response/DTO.
   */
  convertToResponseDensity(value: string): string {
    switch (value) {
      case DENSITY_WITH_ABB.RESIDENTIAL:
        return DENSITY.RESIDENTIAL;
      case DENSITY_WITH_ABB.COMMERCIAL:
        return DENSITY.COMMERCIAL;
      case DENSITY_WITH_ABB.UNKNOWN:
        return DENSITY.UNKNOWN;
      default:
        return DENSITY.COMMERCIAL;
    }
  }

  /** Closes the dialog and prompts the user if the form has unsaved changes. */
  onClose(): void {
    this.commonUtils.openExitModal(this.editTrackingDetailForm.dirty);
  }

  /**
   * Handles keyboard events for package row actions.
   *
   * - Enter: inserts a new package row and applies the current common scan code.
   * - Delete: removes the package row at the given index.
   *
   * @param $event The keyboard event fired from the row.
   * @param itemIndex The index of the row that should be removed on Delete.
   */
  rowKeyPress($event: KeyboardEvent, itemIndex: number) {
    if ($event.key === 'Enter') {
      this.addPackageItem();
      setTimeout(() => {
        this.formDataArray.controls[0]
          .get('scanCode')
          ?.setValue(this.editTrackingDetailForm.get('commonScanCode')?.value);
      }, 50);
    } else if ($event.key === 'Delete') {
      this.removeRow(itemIndex);
    } else {
      return;
    }
  }

  /** Adds a new package row with default controls, validation,
 and focuses the tracking number field.*/
  addPackageItem(): void {
    const newPackageGroup = this.fb.group(
      {
        trackingNumber: ['', Validators.required],
        shipperNumber: [
          '',
          Validators.compose([
            Validators.required,
            Validators.minLength(6),
            Validators.maxLength(6),
          ]),
        ],
        scanCode: [this.editTrackingDetailForm.get('commonScanCode')?.value],
        description: [],
        amtDue: ['0'],
        amtCollected: ['0'],
        paymentType: [this.CONSTANTS.CASH],
        taxidPfcNumber: [],
        taxidPfc: [TAXID_PFC_LIST[0].value],
      },
      {
        validators: [
          this.trackNoValidator.trackingNumberValidator('trackingNumber'),
          this.trackNoValidator.taxIdMinLengthValidator(
            'taxidPfcNumber',
            'taxidPfc',
          ),
        ],
      },
    );

    this.formDataArray.insert(0, newPackageGroup);

    this.commonUtils.autoCapitalize(
      this.formDataArray.at(0) as FormGroup,
      ['trackingNumber', 'shipperNumber', 'description', 'taxidPfcNumber'],
      this.destroy,
    );

    this.scanCodeChanges();
    this.setupInfoNoticeValidation();

    setTimeout(() => {
      const input = this.trackingInputs?.get(0);
      input?.setFocus();
    }, 0);

    this.editTrackingDetailForm.updateValueAndValidity();
    this.hasClickedAdd = true;
  }

  /** Removes a package row and queues the corresponding backend delete call.
   * Handles pickup vs delivery delete APIs, cleans up the form state,
   * and re-focuses the next available row.*/
  removeRow(itemIndex: number) {
    this.commonUtils
      .openWarningModal({
        header: this.CONSTANTS.WARNING_MODAL_FOR_DELETE.HEADER,
        body: this.CONSTANTS.WARNING_MODAL_FOR_DELETE.BODY,
        acceptButtonText:
          this.CONSTANTS.WARNING_MODAL_FOR_DELETE.CONFIRM_BUTTON,
        cancelButtonText: this.CONSTANTS.WARNING_MODAL_FOR_DELETE.REJECT_BUTTON,
      })
      .afterClosed()
      .subscribe((resp) => {
        if (resp.button === BUTTON_CLICK.ACCEPT) {
          this.hasClickedDelete = true;

          const isPickup = this.data.routeDetails[0].type === STOP_TYPE_ABB.PS;
          const formItem = this.formDataArray.at(itemIndex);

          const isDuplicate = this.isDuplicateTrackingAtIndex(itemIndex);
          if (isDuplicate) {
            // Skip API delete. Remove locally only.
            this.removePackage(itemIndex);
            this.commonUtils.autoFocusOnNewInput(this.formDataArray.length * 5);

            // Keep form state consistent
            this.formDataArray.updateValueAndValidity();
            this.formDataArray.markAsDirty();
            this.editTrackingDetailForm.updateValueAndValidity();
            return;
          }

          let deleteCall$!: Observable<any>;

          if (isPickup) {
            deleteCall$ = this.pickupService
              .deletePickup(
                this.createPickupRequestForDelete(formItem),
                this.locationCode,
                this.data.route,
              )
              .pipe(take(1));
          } else {
            deleteCall$ = this.deliveryService
              .deleteDelivery(
                this.createDeliveryRequestForDelete(formItem),
                this.locationCode,
                this.data.route,
              )
              .pipe(take(1));
          }

          // Execute delete immediately
          deleteCall$.subscribe({
            next: () => {
              // delete succeeded → remove locally
              this.removePackage(itemIndex);
              this.commonUtils.autoFocusOnNewInput(
                this.formDataArray.length * 5,
              );

              this.setupInfoNoticeValidation();

              this.formDataArray.updateValueAndValidity();
              this.formDataArray.markAsDirty();
              this.editTrackingDetailForm.updateValueAndValidity();
            },
            error: (err) => {
              console.error('Delete failed:', err);
            },
          });
        }
      });
  }

  /**
   * Returns true if the row at itemIndex is part of a duplicate-tracking-number set.
   * Works with multiple validator wiring patterns:
   *  - Control-level error: trackingNumber.hasError('uniqueTrackingNumbers')
   *  - Array-level error: formArray.errors?.uniqueTrackingNumbers with indices or a map
   *  - Fallback: local scan of duplicates
   */
  private isDuplicateTrackingAtIndex(itemIndex: number): boolean {
    const row = this.formDataArray.at(itemIndex) as FormGroup;
    const trackingCtrl = row.get('trackingNumber');

    if (trackingCtrl?.hasError?.('uniqueTrackingNumbers')) {
      return true;
    }

    const arrErrors = this.formDataArray.errors;
    if (arrErrors && arrErrors['uniqueTrackingNumbers']) {
      const meta = arrErrors['uniqueTrackingNumbers'];
      if (Array.isArray(meta?.indices)) {
        if (meta.indices.includes(itemIndex)) return true;
      }
      if (meta?.map && typeof meta.map === 'object') {
        const value = trackingCtrl?.value;
        const indices: number[] = meta.map[value] || [];
        if (indices.length > 1 && indices.includes(itemIndex)) return true;
      }
    }

    return this.isLocallyDuplicate(itemIndex);
  }

  /**
   * Local duplicate detector (fallback): counts occurrences of the value across the array.
   */
  private isLocallyDuplicate(itemIndex: number): boolean {
    const valueAtIndex = (this.formDataArray.at(itemIndex) as FormGroup).get(
      'trackingNumber',
    )?.value;

    if (valueAtIndex == null || valueAtIndex === '') return false;

    let count = 0;
    for (let i = 0; i < this.formDataArray.length; i++) {
      const v = (this.formDataArray.at(i) as FormGroup).get(
        'trackingNumber',
      )?.value;
      if (v === valueAtIndex) count++;
      if (count > 1) return true; // early exit
    }
    return false;
  }

  /** Constructs and returns a CreatePickupRequest object based on the form data and route details.
   * Returns null if the delete action has been triggered.*/
  createPickupRequest(): CreatePickupRequest | null {
    const pickupPackages: PickupPackage[] = [];
    const packageControls = this.formDataArray?.getRawValue();
    const data = this.data.routeDetails[0];

    const localStopDate = this.editTrackingDetailForm.get('stopDate')?.value;
    const localCompleteTime =
      this.editTrackingDetailForm.get('completionTime')?.value;
    const stopTime = this.createStopTime(localStopDate, localCompleteTime);

    packageControls.forEach((packageControl) => {
      pickupPackages.push({
        trackingNumber: packageControl.trackingNumber,
        shipperAccount: packageControl.shipperNumber,
        scanCode: packageControl.scanCode,
        remark: packageControl.description,
        paymentDue: { amount: packageControl.amtDue },
        payment: {
          amount: packageControl.amtCollected,
          type: packageControl.paymentType,
        },
        taxId: packageControl.taxidPfcNumber,
        position: packageControl.position,
      });
    });

    const pickupDetail: any = {
      scanCode: this.editTrackingDetailForm.get('noPackagePickupStatus')?.value,
      pickupRequestNumber: data.prn
        ? data.prn
        : this.editTrackingDetailForm.get('pickupRequestNumber')?.value,
      pickupType: this.editTrackingDetailForm.get('pickupRequestNumber')?.value
        ? 'ONROAD'
        : '',
      infoNotice: this.editTrackingDetailForm.get('infoNotice')?.value ?? null,
    };

    const pickupRequest: CreatePickupRequest = {
      stop: {
        stopId: data.id,
        address: {
          city: data.city,
          consignee: data.cosignee,
          country: this.countryCode$.getValue(),
          postalCode: data.postal,
          state: data.province,
          address1: data.address.trim().substring(0, 30),
        },
        completed: !!localCompleteTime,
        density: this.convertToResponseDensity(
          this.editTrackingDetailForm.get('density')?.value,
        ),
        loop: this.data.route,
        planSequence: 1,
        userId: this.user,
        stopType: STOP_TYPE.PICKUP,
        planDate: new Date(
          formatDate(
            this.editTrackingDetailForm.get('planDate')?.value,
            'yyyy-MM-dd',
            'en-US',
          ),
        ),
        stopTime,
      },
      pickupPackages: this.editTrackingDetailForm.get('noPackagePickupStatus')
        ?.value
        ? []
        : pickupPackages,
      pickupDetail,
    };

    return pickupRequest;
  }

  /** Generates a CreatePickupRequest object for a single package marked for deletion,
   * using the provided package control and current form/route details.*/
  createPickupRequestForDelete(deletePackageControl: any): CreatePickupRequest {
    const pickupPackages: PickupPackage[] = [];
    const packageControl = deletePackageControl?.getRawValue();
    const data = this.data.routeDetails[0];
    const localStopDate = this.editTrackingDetailForm.get('stopDate')?.value;
    const localCompleteTime =
      this.editTrackingDetailForm.get('completionTime')?.value;
    const stopTime = this.createStopTime(localStopDate, localCompleteTime);

    pickupPackages.push({
      trackingNumber: packageControl.trackingNumber,
      shipperAccount: packageControl.shipperNumber,
      scanCode: packageControl.scanCode,
      remark: packageControl.description,
      paymentDue: {
        amount: packageControl.amtDue,
      },
      payment: {
        amount: packageControl.amtCollected,
        type: packageControl.paymentType,
      },
      taxId: packageControl.taxidPfcNumber,
      position: packageControl.position,
    });

    const pickupRequest: CreatePickupRequest = {
      stop: {
        stopId: data.id,
        address: {
          city: data.city,
          consignee: data.cosignee,
          country: this.countryCode$.getValue(),
          postalCode: data.postal,
          state: data.province,
          address1: data.address.trim().substring(0, 30),
        },
        completed: !!localCompleteTime,
        density: this.convertToResponseDensity(
          this.editTrackingDetailForm.get('density')?.value,
        ),
        loop: this.data.route,
        planSequence: 1,
        userId: this.user,
        stopType: STOP_TYPE.PICKUP,
        planDate: new Date(
          formatDate(
            this.editTrackingDetailForm.get('planDate')?.value,
            'yyyy-MM-dd',
            'en-US',
          ),
        ),
        stopTime: stopTime,
      },
      pickupPackages: this.editTrackingDetailForm.get('noPackagePickupStatus')
        ?.value
        ? []
        : pickupPackages,
      pickupDetail: {
        scanCode: this.editTrackingDetailForm.get('noPackagePickupStatus')
          ?.value,
        pickupRequestNumber: data.prn
          ? data.prn
          : this.editTrackingDetailForm.get('pickupRequestNumber')?.value,
        pickupType: this.editTrackingDetailForm.get('pickupRequestNumber')
          ?.value
          ? 'ONROAD'
          : '',
        infoNotice:
          this.editTrackingDetailForm.get('infoNotice')?.value ?? null,
      },
    };

    return pickupRequest;
  }

  /** Constructs and returns a CreateDeliveryRequest object based on the form data and route details.
   * Returns null if the delete action has been triggered.*/
  createDeliveryRequest(): CreateDeliveryRequest | null {
    const deliveryPackages: DeliveryPackage[] = [];
    const packageFormData = this.formDataArray?.getRawValue();
    const data = this.data.routeDetails[0];

    const localStopDate = this.editTrackingDetailForm.get('stopDate')?.value;
    const localCompleteTime =
      this.editTrackingDetailForm.get('completionTime')?.value;
    const stopTime = this.createStopTime(localStopDate, localCompleteTime);

    packageFormData.forEach((packageControl) => {
      deliveryPackages.push({
        trackingNumber: packageControl.trackingNumber,
        shipperAccount: packageControl.shipperNumber,
        scanCode: packageControl.scanCode,
        remark: packageControl.description,
        paymentDue: {
          amount: packageControl.amtDue,
        },
        payment: {
          amount: packageControl.amtCollected,
          type: packageControl.paymentType,
        },
        taxId: packageControl.taxidPfcNumber,
        position: packageControl.position,
      });
    });

    const density = this.editTrackingDetailForm.get('density')?.value;
    const deliveryDetail: any = {
      signatureConfirm: this.editTrackingDetailForm.get('clarify')?.value,
      infoNotice: this.editTrackingDetailForm.get('infoNotice')?.value ?? null,
    };

    const deliveryRequest: CreateDeliveryRequest = {
      stop: {
        stopId: data.id,
        address: {
          city: data.city,
          consignee: data.cosignee,
          country: this.countryCode$.getValue(),
          postalCode: data.postal,
          state: data.province,
          address1: data.address.trim().substring(0, 30),
        },
        completed: !!localCompleteTime,
        density: density.includes('-')
          ? this.convertToResponseDensity(density)
          : density,
        loop: this.data.route,
        planDate: new Date(
          formatDate(
            this.editTrackingDetailForm.get('planDate')?.value,
            'yyyy-MM-dd',
            'en-US',
          ),
        ),
        stopTime: stopTime,
        planSequence: 1,
        userId: this.user,
        stopType: STOP_TYPE.DELIVERY,
      },
      deliveryPackages: deliveryPackages,
      deliveryDetail,
    };

    return deliveryRequest;
  }

  /** Creates a CreateDeliveryRequest object for a single package marked for deletion,
   * using the provided package control and current form/route details.*/
  createDeliveryRequestForDelete(
    deletePackageControl: any,
  ): CreateDeliveryRequest {
    const deliveryPackages: DeliveryPackage[] = [];
    const packageControl = deletePackageControl?.getRawValue();
    const data = this.data.routeDetails[0];

    const localStopDate = this.editTrackingDetailForm.get('stopDate')?.value;
    const localCompleteTime =
      this.editTrackingDetailForm.get('completionTime')?.value;
    const stopTime = this.createStopTime(localStopDate, localCompleteTime);

    deliveryPackages.push({
      trackingNumber: packageControl.trackingNumber,
      shipperAccount: packageControl.shipperNumber,
      scanCode: packageControl.scanCode,
      remark: packageControl.description,
      paymentDue: {
        amount: packageControl.amtDue,
      },
      payment: {
        amount: packageControl.amtCollected,
        type: packageControl.paymentType,
      },
      taxId: packageControl.taxidPfcNumber,
      position: packageControl.position,
    });

    const density = this.editTrackingDetailForm.get('density')?.value;
    const deliveryRequest: CreateDeliveryRequest = {
      stop: {
        stopId: data.id,
        address: {
          city: data.city,
          consignee: data.cosignee,
          country: this.countryCode$.getValue(),
          postalCode: data.postal,
          state: data.province,
          address1: data.address.trim().substring(0, 30),
        },
        completed: !!localCompleteTime,
        density: density.includes('-')
          ? this.convertToResponseDensity(density)
          : density,
        loop: this.data.route,
        planDate: new Date(
          formatDate(
            this.editTrackingDetailForm.get('planDate')?.value,
            'yyyy-MM-dd',
            'en-US',
          ),
        ),
        stopTime: stopTime,
        planSequence: 1,
        userId: this.user,
        stopType: STOP_TYPE.DELIVERY,
      },
      deliveryPackages: deliveryPackages,
      deliveryDetail: {
        signatureConfirm: this.editTrackingDetailForm.get('clarify')?.value,
      },
    };

    return deliveryRequest;
  }

  /** Combines the provided stop date and completion time, applies the location's timezone,
   * and returns the stop time formatted in UTC (ISO string).
   * Returns null if either localStopDate or localCompleteTime is missing.*/
  createStopTime(localStopDate: any, localCompleteTime: any): any {
    let stopTime = null;
    if (localStopDate && localCompleteTime) {
      this.locationService
        .getCurrentLocation$()
        .pipe(takeUntilDestroyed(this.destroy))
        .subscribe((location) => {
          this.locationTimezone = location.location?.gmtTimeZone ?? '';
          const timezone = this.locationTimezone.split(' ')[0];

          const stopDate = new Date(localStopDate);
          const [hours, minutes] = localCompleteTime.split(':').map(Number);
          stopDate.setHours(hours, minutes);

          // Parse the localStopDate as a moment object
          const momentStopDate = moment(
            stopDate,
            'ddd MMM DD YYYY HH:mm:ss [GMT]ZZ',
          );

          // Remove the timezone by formatting it without timezone information
          const formattedDate = momentStopDate.format(
            'YYYY-MM-DDTHH:mm:ss.SSS',
          );

          const dateWithTimezone = moment.tz(
            formattedDate,
            'YYYY-MM-DDTHH:mm:ss.SSS',
            timezone,
          );

          stopTime = dateWithTimezone
            .clone()
            .utc()
            .format('YYYY-MM-DDTHH:mm:ss.SSS[Z]');
        });
    }
    return stopTime;
  }

  /** Finalizes the current stop by creating and submitting either a delivery or pickup request,
   * processes any pending delete calls, and closes the dialog with the response.
   * Updates loading and banner states based on API results.*/
  completeStop(): void {
    // Delivery-only FE validation for infoNotice
    const isDeliveryStop = this.data.routeDetails[0].type === STOP_TYPE_ABB.DS;

    if (!this.canComplete) return;

    this.commonUtils.updateLoadingModal(true);

    const hasFormData = this.formDataArray.length > 0;
    const deliveryRequest = this.createDeliveryRequest();
    const pickupRequest = this.createPickupRequest();

    let stopCall$!: Observable<any>;
    let request!: any;

    // DELIVERY
    if (hasFormData && isDeliveryStop && !!deliveryRequest) {
      stopCall$ = this.deliveryService.createDelivery(deliveryRequest).pipe(
        take(1),
        map((res) => {
          this.deliveryResponse = res;
          request = deliveryRequest;
          return res;
        }),
      );
    }
    // PICKUP
    else if (!isDeliveryStop && pickupRequest) {
      stopCall$ = this.pickupService
        .createPickup(pickupRequest, this.locationCode, this.data.route)
        .pipe(
          take(1),
          map((res) => {
            this.pickupResponse = res;
            request = pickupRequest;
            return res;
          }),
        );
    }
    // NO FORM DATA
    else {
      stopCall$ = of({ code: 200 }).pipe(take(1));
    }

    stopCall$.pipe(take(1)).subscribe({
      next: (res) => {
        this.commonUtils.updateLoadingModal(false);
        if (res?.code === 200) {
          // Success
          if (isDeliveryStop) {
            const stopId = this.data.routeDetails[0].id;
            this.dataService.clearInfoNotice(stopId);
          }
          this.commonUtils.updateBannerType(BANNER_TYPE.API_SUCCESS);
          this.dialogRef.close({ request, response: res });
        } else {
          this.commonUtils.updateBannerType(BANNER_TYPE.API_400_ERROR);
          this.dialogRef.close();
        }
      },
      error: () => {
        this.commonUtils.updateLoadingModal(false);
        this.commonUtils.updateBannerType(BANNER_TYPE.API_400_ERROR);
      },
    });
  }

  /** Clears the taxidPfcNumber field when the taxidPfc dropdown selection changes.*/
  onTaxIdTypeChange(rowIndex: number): void {
    const taxIdNumberControl = this.formDataArray
      .at(rowIndex)
      .get('taxidPfcNumber');
    if (taxIdNumberControl) {
      taxIdNumberControl.setValue('');
      taxIdNumberControl.markAsUntouched();
      taxIdNumberControl.markAsPristine();
    }
  }

  /** Prevents entry of special characters except alphabets and numbers.*/
  preventTaxSpecialCharacters(event: KeyboardEvent): boolean {
    return this.commonUtils.preventSpecialCharacters(
      event,
      SpecialCharacters.ALPHABET_NUMBER,
    );
  }

  /** Prevents entry of special characters except dots.*/
  preventSpecialCharacters(event: KeyboardEvent): boolean {
    return this.commonUtils.preventSpecialCharacters(
      event,
      SpecialCharacters.DOT,
    );
  }

  /** Prevents entry of special characters except dots and spaces.*/
  preventSpecialCharactersWithSpaces(event: KeyboardEvent): boolean {
    return this.commonUtils.preventSpecialCharacters(
      event,
      SpecialCharacters.DOT_SPACE_ALLOW,
    );
  }

  /** Saves the form by processing delete calls first, then submitting
   * either a pickup or delivery request depending on the stop type.
   * Updates loading state and shows success banner after completion.*/
  saveForm(): void {
    // Delivery-only FE validation
    const isDeliveryStop = this.data.routeDetails[0].type === STOP_TYPE_ABB.DS;
    if (isDeliveryStop) {
      const infoCtrl = this.editTrackingDetailForm.get('infoNotice');
      if (infoCtrl?.invalid) {
        this.commonUtils.updateBannerType(BANNER_TYPE.API_400_ERROR);
        return;
      }
    }

    if (!this.canSave) return;

    this.commonUtils.updateLoadingModal(true);

    const hasFormData = this.formDataArray.length > 0;

    let request: any = null;
    let pickupOrDelivery$!: Observable<any>;

    if (hasFormData) {
      if (isDeliveryStop) {
        request = this.createDeliveryRequest();
        if (!request) {
          this.commonUtils.updateLoadingModal(false);
          this.commonUtils.updateBannerType(BANNER_TYPE.API_400_ERROR);
          return;
        }
        pickupOrDelivery$ = this.createDelivery(request);
      } else {
        request = this.createPickupRequest();
        if (!request) {
          this.commonUtils.updateLoadingModal(false);
          this.commonUtils.updateBannerType(BANNER_TYPE.API_400_ERROR);
          return;
        }
        pickupOrDelivery$ = this.createPickup(request);
      }
    } else {
      // No row-level changes; allow a save without API
      pickupOrDelivery$ = of({ code: 200 });
    }

    pickupOrDelivery$.pipe(take(1)).subscribe({
      next: (res) => {
        this.commonUtils.updateLoadingModal(false);
        if (res?.code === 200) {
          // Cache infoNotice for Delivery only
          if (isDeliveryStop) {
            const stopId = this.data.routeDetails[0].id;
            const info = (
              this.editTrackingDetailForm.get('infoNotice')?.value ?? ''
            ).toString();
            this.dataService.setInfoNotice(stopId, info);
          }
          this.commonUtils.updateBannerType(BANNER_TYPE.API_SUCCESS);
          this.dialogRef.close({ request, response: res });
        } else {
          this.commonUtils.updateBannerType(BANNER_TYPE.API_400_ERROR);
        }
      },
      error: () => {
        this.commonUtils.updateLoadingModal(false);
        this.commonUtils.updateBannerType(BANNER_TYPE.API_400_ERROR);
      },
    });
  }

  /** Creates and submits a pickup request if form data exists.
   * Returns an Observable of the API response, or a fake success if no data.*/
  createPickup(request: CreatePickupRequest): Observable<CreatePickupResponse> {
    return this.pickupService
      .createPickup(request, this.locationCode, this.data.route)
      .pipe(
        take(1),
        map((res) => {
          this.pickupResponse = res;
          return res;
        }),
      );
  }

  /** Creates and submits a delivery request if form data exists.
   * Returns an Observable of the API response, or a fake success if no data.*/
  createDelivery(
    request: CreateDeliveryRequest,
  ): Observable<CreateDeliveryResponse | { code: number }> {
    return this.deliveryService.createDelivery(request).pipe(
      take(1),
      map((res) => {
        this.deliveryResponse = res;
        return res;
      }),
    );
  }

  /** Returns the controls of the 'data' FormArray in the editTrackingDetailForm.*/
  getControls() {
    return (this.editTrackingDetailForm.get('data') as FormArray).controls;
  }

  /** Highlights the border of the specified input field on focus.*/
  focusAmount(fieldName: string) {
    const field = document.getElementById(fieldName);
    if (field) {
      field.classList.add('amount-focused');
    }
  }

  /** Resets the border of the specified input field on blur.*/
  unFocusAmount(fieldName: string) {
    const field = document.getElementById(fieldName);
    if (field) {
      field.classList.remove('amount-focused');
    }
  }

  /** Prevents input exceeding max allowed value and restricts to decimal numbers.*/
  preventMaxLimitValue(event: KeyboardEvent) {
    const allowedControlKeys = [
      'Backspace',
      'Delete',
      'Tab',
      'Escape',
      'Enter',
      'ArrowLeft',
      'ArrowRight',
      'Home',
      'End',
    ];

    const key = event.key;

    // Allow control/navigation keys
    if (allowedControlKeys.includes(key) || event.ctrlKey || event.metaKey) {
      return true;
    }

    // Allow only digits and dot
    if (!/[0-9.]/.test(key)) {
      event.preventDefault();
      return false;
    }

    // Get current value from input
    const input = event.target as HTMLInputElement;
    const value = input.value;

    // If key is '.' and value already contains a dot → block
    if (key === '.' && value.includes('.')) {
      event.preventDefault();
      return false;
    }

    // If value already has a dot, check decimal part length
    if (value.includes('.')) {
      const [, decimalPart] = value.split('.');
      if (decimalPart && decimalPart.length >= 2 && key !== '.') {
        // Already 2 digits after dot → block further decimals
        event.preventDefault();
        return false;
      }
    }

    return true;
  }

  /** Adds the 'stopDate' control to the form if it doesn't already exist.*/
  updateTime() {
    if (!this.editTrackingDetailForm.get('stopDate')) {
      this.editTrackingDetailForm.addControl(
        'stopDate',
        new FormControl(new Date(), Validators.required),
      );
    }
  }

  /** Placeholder for getting delivery detail. Currently returns an empty object.*/
  getDeliveryDetail() {
    return {};
  }

  /** Updates shipper ID, COD amount, and validates intercepted tracking numbers
   * based on the provided tracking number at the specified index.*/
  updateTrackingNumber(i: number): void {
    const group = this.formArray.controls[i];
    const trackingNumberCtrl = group.get('trackingNumber');
    const shipperIdCtrl = group.get('shipperNumber');
    const amtDueCtrl = group.get('amtDue');

    const tn = (trackingNumberCtrl?.value ?? '')
      .toString()
      .trim()
      .toUpperCase();
    trackingNumberCtrl?.setValue(tn, { emitEvent: false });

    const trackingNumber = trackingNumberCtrl?.value as string;

    // If basic validation fails
    if (
      !trackingNumberCtrl?.valid ||
      trackingNumber?.length <= 8 ||
      !trackingNumber.startsWith('1Z')
    ) {
      return;
    }

    const shipperSegment = trackingNumber.substring(2, 8);
    const codCheckSegment = trackingNumber.substring(8, 10);

    // Set shipper ID
    shipperIdCtrl?.setValue(shipperSegment);

    const isDeliveryStop =
      this.data.routeDetails?.[0]?.type === STOP_TYPE_ABB.DS;

    // COD required logic
    if (isDeliveryStop && this.codRequiredResponse.includes(codCheckSegment)) {
      amtDueCtrl?.setValue('');
      amtDueCtrl?.setValidators(codAmountValidator);
      amtDueCtrl?.markAsTouched();
      amtDueCtrl?.updateValueAndValidity();
    } else {
      amtDueCtrl?.setValue(0);
      amtDueCtrl?.clearValidators();
      amtDueCtrl?.markAsTouched();
      amtDueCtrl?.updateValueAndValidity();
    }

    // Intercepted package logic
    if (
      isDeliveryStop &&
      this.interceptedPackageResponse?.includes(codCheckSegment)
    ) {
      trackingNumberCtrl.setErrors({ interceptedPackage: true });
    }

    // Validating package for intercepted tracking number
    if (isDeliveryStop && trackingNumber) {
      this.deliveryService
        .validateDeliveryPackage([trackingNumber])
        .pipe(takeUntilDestroyed(this.destroy))
        .subscribe((res) => {
          if (res.errors) {
            this.handleErrorCheck(res.errors);
            trackingNumberCtrl?.markAsTouched();
            trackingNumberCtrl?.markAsDirty();
          } else {
            if (trackingNumberCtrl?.hasError('intercepted')) {
              const errors = { ...trackingNumberCtrl.errors };
              delete errors.intercepted;

              trackingNumberCtrl.setErrors(
                Object.keys(errors).length ? errors : null,
              );
            }
          }
        });
    }

    this.formArray.updateValueAndValidity({
      onlySelf: false,
      emitEvent: true,
    });
  }

  /** Returns whether the 'amtDue' field at index i is required.*/
  isAmtRequired(i: number) {
    return this.formArray
      .at(i)
      .get('amtDue')
      ?.hasValidator(Validators.required);
  }

  /** Checks scan codes in the form array and sets validation for signature confirmation.*/
  scanCodeChanges() {
    const scanCode = this.formArray.value.find((item: any) => {
      return (
        item.scanCode === SCAN_CODES.SIG_OBTAINED.toUpperCase() ||
        item.scanCode === SIG_OBTAINED_CODE
      );
    })?.scanCode;

    this.setClarifyValidation(scanCode as string);
  }

  /** Returns the 'data' FormArray from the editTrackingDetailForm.*/
  get formDataArray(): FormArray {
    return <FormArray>this.editTrackingDetailForm.get('data');
  }

  /** Transforms route details into form data structure for FormArray.*/
  private transformDataToFormData(
    data: TableOperationsRouteDetails[],
  ): FormData {
    return {
      data: data.map(
        (d: TableOperationsRouteDetails) =>
          ({
            trackingNumber: d.trackingNumber,
            shipperNumber: d.shipperNumber,
            scanCode: d.scanCode,
            description: d.description,
            amtDue: this.getAmountDue(d),
            amtCollected: d.amtCollected,
            paymentType: d.paymentType,
            position: d.position,
          }) as TableOperationsRouteDetails,
      ),
    };
  }

  /** Removes a package from the form array and updates form validity and flags.*/
  private removePackage(itemIndex: number) {
    this.formDataArray.removeAt(itemIndex);
    if (this.formDataArray.length === 0) {
      this.packageDetailsExists = false;
    }

    this.scanCodeChanges();

    this.editTrackingDetailForm.markAsDirty();
    this.editTrackingDetailForm.markAsTouched();
    this.formDataArray.updateValueAndValidity();
    this.editTrackingDetailForm.updateValueAndValidity();
  }

  /** Calculates the span for UI table rendering, adjusting for scroll if needed.*/
  private getSpan(field: number, offset = 0) {
    //Check for needed offset if scroll bar is present
    return this.formArray.length > 5 ? field - offset : field;
  }

  /** Checks whether any tracking numbers exist in the form array and updates the package existence subject.*/
  private checkPickupStatus() {
    const dataArray = this.editTrackingDetailForm.get('data') as FormArray;
    const items = dataArray.getRawValue(); // includes disabled fields
    const exists = items.some((item) => !!item.trackingNumber);
    this.packageDetailsExistsSubject.next(exists);
  }

  /** Handles server-returned tracking errors and applies required UI actions.
   * Logic performed:
   * 1. Split the returned errors by type using predefined backend message constants.
   * 2. If Enterprise errors exist:
   *    - Show “Do Not Process” modal dialog.
   *    - After user acknowledges the dialog, remove the affected packages from the form.
   * 3. For Intercept and Invalid Tracking Number errors:
   *    - Mark the respective trackingNumber form controls with specific validation errors
   *      so UI can display field-level error feedback.
   *
   * @param res - Array of errors returned from backend containing
   *              message and tracking information.
   *
   * Side Effects:
   * - Removes package form rows (if enterprise errors exist)
   * - Sets Angular form control validation errors on trackingNumber fields
   * - Opens a blocking modal dialog*/
  private handleErrorCheck(res: InterceptError[]) {
    const enterpriseErrors = res.filter(
      (val) => val.message === EnterpriseErrorMessage,
    );
    const interceptErrorNumbers = res
      .filter((val) => val.message.startsWith(InterceptErrorMessage))
      .map((val) => val.trackingNumber);

    const trackingErrorNumbers = res
      .filter((val) => val.message.startsWith(InvalidNumberMessage))
      .map((val) => val.trackingNumber);

    if (enterpriseErrors.length > 0) {
      this.commonUtils
        .openDoNotProcessModal(enterpriseErrors)
        .afterClosed()
        .pipe(take(1))
        .subscribe(() => {
          res.forEach((val) => {
            this.formDataArray.controls.forEach((pkgFormGroup, i) => {
              const trackingNumber = pkgFormGroup.get('trackingNumber')?.value;
              if (val.trackingNumber.includes(trackingNumber)) {
                this.removePackage(i);
              }
            });
          });
        });
    }

    this.formDataArray.controls.forEach((pkgFormGroup) => {
      const control = pkgFormGroup.get('trackingNumber');

      if (interceptErrorNumbers.includes(control?.value)) {
        control?.setErrors({ intercepted: true });
      }

      if (trackingErrorNumbers.includes(control?.value)) {
        control?.setErrors({ invalidTrackingNo: true });
      }
    });
  }
}
